import BaseBoosterBehavior, { IBaseBoosterBehaviorPlayMeta } from './BaseBoosterBehavior';
import CardStateHelper, { getCardsState } from '../CardStateHelper';
import { CardType } from '../../../Prototype/Level/Card/CardType';
import { Random } from '../../../Utility/Random';
import { CardTask } from '../CardTasks';
import { SCORE_BULLSEYE_BOOSTER } from '../../../Constants/Score';
import { BoosterType } from '../BoosterType';
import { track, TrackMethod } from '../../../Utility/Tracking/Track';
import { isNearEndGame } from '../../Level/LevelStateHelper';
import Game from '../../../Game';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import { TransactionType } from '../../../Utility/Tracking/DataSet/Transaction';
import { IGameState } from '../../Game/IGameState';
import { ICardsState } from '../ICardState';
import CardStateModifierHelper from '../CardStateModifierHelper';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';

export default class BullsEyeBoosterBehavior extends BaseBoosterBehavior {
    public play(state: IGameState, meta?: IBaseBoosterBehaviorPlayMeta): IGameState {
        // If there are not enough target cards, do nothing.
        if (!this.hasEnoughTargetCards(state)) return state;

        // Find all value cards that are face-up and not obscured.
        const possibleTargets = this.getPossibleTargetCards(state);

        this.addCardToTargets(
            state.levelState.cardsState,
            possibleTargets[Random.int(0, possibleTargets.length - 1)],
            true
        );

        let cardsState = getCardsState(state);
        this.targetIDs.forEach(target => {
            // Discard target card.
            state = CardStateHelper.setCardDiscarded(state, target);
            cardsState = getCardsState(state);

            // Using Bullseye booster restart streak score.
            cardsState = CardStateHelper.cancelStreakScore(cardsState);

            // Increment streak.
            cardsState = CardStateHelper.increaseStreak(cardsState, SCORE_BULLSEYE_BOOSTER, target);

            // Add turn score.
            cardsState = CardStateHelper.addTurnScore(cardsState, cardsState.streakScore);

            state = CardStateHelper.updateGameState(state, cardsState);

            state = CardStateModifierHelper.onCardPlayed(state, target);
        });

        cardsState = getCardsState(state);
        // Task for animation.
        cardsState = CardStateHelper.setStateTaskData(cardsState, {
            task: CardTask.BULLS_EYE,
            cards: this.targetIDs,
            score: cardsState.streakScore,
        });

        // Check if cards need to be flipped.
        cardsState = CardStateHelper.checkCardsObscured(cardsState);

        state = CardStateHelper.updateGameState(state, cardsState);

        // Check for streak bonus.
        cardsState = CardStateHelper.checkStreakBonus(state);

        // Clear the undo history when a joker is played.
        cardsState = CardStateHelper.clearUndoHistory(cardsState);

        state = CardStateHelper.updateGameState(state, cardsState);

        state = this.incrementBoosterUse(state, BoosterType.BULLS_EYE);

        return this.consumeItemOrCoins(state);
    }

    public getPossibleTargetCards(state: IGameState): string[] {
        const cardsState: ICardsState = { ...state.levelState.cardsState };

        return cardsState.inPlay.filter((cardID: string) => {
            const behavior = CardStateHelper.getCardBehavior(cardsState, cardID);
            return (
                (cardsState.cards[cardID].type === CardType.value || cardsState.cards[cardID].type === CardType.gold) &&
                !behavior.isBlocked(cardsState)
            );
        });
    }

    public hasEnoughTargetCards(state: IGameState): boolean {
        return this.getPossibleTargetCards(state).length > 0;
    }

    protected getPrice(state: IGameState): number {
        return state.levelState.boosterPrices[BoosterType.BULLS_EYE];
    }

    protected getInventoryItem(state: IGameState): number {
        return state.playerState.inventory[BoosterType.BULLS_EYE];
    }

    protected spendInventoryItem(state: IGameState): IGameState {
        track('transactionType', TransactionType.INVENTORY);

        // TODO: use InventoryStateHelper.consumeInventory instead
        return {
            ...state,
            playerState: InventoryStateHelper.setBoosterBullsEye(
                state.playerState,
                state.playerState.inventory[BoosterType.BULLS_EYE] - 1,
                { queueTask: false }
            ),
        };
    }

    protected spendCoins(state: IGameState): IGameState {
        super.trackCoinsSpent(state, 'tRemoveOneCoins');
        track('tRemoveOneReceived', 1, TrackMethod.ADD);
        state = super.spendCoins(state);
        track('tRemoveOneUsed', 1, TrackMethod.ADD);
        return state;
    }

    protected consumeItemOrCoins(state: IGameState): IGameState {
        Game.sdk.gameTracking(FBTrackingEvent.TapOnSlingShot);
        track('mRemoveOneUsed', 1, TrackMethod.ADD);

        if (isNearEndGame(state)) {
            track('mNumBoostersUsedLossAversion', 1, TrackMethod.ADD);
        }
        return super.consumeItemOrCoins(state);
    }
}
